import { useNode } from "@craftjs/core";
import React from "react";

import {
  ColorInput,
  UnitInput,
  BoxShadowInput,
  PaddingInput,
  Panel,
  Tabs,
  TextInput,
  BorderRadiusInput,
  BorderInput,
  AlignInput,
  ToggleInput,
  NumberInput,
  SelectInput,
  MarginInput,
} from "../../input";
import { isUpdatedFromDefault } from "../../utils";

import { supportLibrary } from "../../block-supports";
import { INPUT_LIMITS } from "../../input-limits";

export const FormTextareaSettings = () => {
  const {
    id,
    actions: { setProp },
    props,
    dom,
    supports,
  } = useNode((node) => ({
    id: node.id,
    text: node.data.props.text,
    props: node.data.props,
    dom: node.dom,
    supports: node.data?.type?.craft?.related?.supports,
  }));

  const {
    containerWidth,
    containerWidthTablet,
    containerWidthMobile,
    containerHeight,
    containerHeightTablet,
    containerHeightMobile,
    containerMaxWidth,
    align,
    alignTablet,
    alignMobile,
    containerMaxHeight,
    placeholderText,
    inputWidth,
    inputWidthTablet,
    inputWidthMobile,
    inputHeight,
    inputHeightTablet,
    inputHeightMobile,
    inputMaxWidth,
    inputMaxHeight,
    containerBackground,
    inputColor,
    inputPlaceholderColor,
    inputBackgroundColor,
    containerPadding,
    containerPaddingTablet,
    containerPaddingMobile,
    containerBorder,
    containerBorderRadius,
    containerBoxShadow,
    layoutMargin,
    layoutMarginTablet,
    layoutMarginMobile,
    inputMargin,
    inputMarginTablet,
    inputMarginMobile,
    inputPadding,
    inputPaddingTablet,
    inputPaddingMobile,
    inputBorder,
    inputBorderRadius,
    inputBoxShadow,
    autofocus,
    cols,
    disabled,
    maxlength,
    name,
    readonly,
    required,
    rows,
    wrap,
    value,
    showLabel,
    label,
    labelColor,
    labelRequiredColor,
  } = props;

  function GeneralSettings() {
    return (
      <div className="g-settings__wrapper">
        <Panel
          isUpdated={isUpdatedFromDefault([
            { key: name },
            { key: maxlength },
            { key: autofocus },
            { key: disabled },
            { key: required },
            { key: readonly },
            { key: wrap },
            { key: cols },
            { key: rows },
            { key: value },
            { key: alignTablet },
            { key: alignMobile },
            { key: align },
            { key: placeholderText },
            { key: inputWidth },
            { key: inputWidthMobile },
            { key: inputWidthTablet },
            { key: inputHeight },
            { key: inputHeightMobile },
            { key: inputHeightTablet },
          ])}
          id={`block-setting-${id}-input`}
          title={"Input"}
          initialOpen={true}
        >
          <TextInput
            minLength={INPUT_LIMITS["form-input-name"].minlength}
            maxLength={INPUT_LIMITS["form-input-name"].maxlength}
            value={name}
            onChange={(val) => {
              setProp((props) => {
                props.name = val;
              });
            }}
            label={"Name"}
          />

          <NumberInput
            maxLength={INPUT_LIMITS["form-input-name"].maxlength}
            value={maxlength}
            onChange={(val) => {
              setProp((props) => {
                props.maxlength = val;
              });
            }}
            label={"Max Length"}
          />
          <ToggleInput
            value={autofocus}
            onChange={(val) => {
              setProp((props) => {
                props.autofocus = val;
              });
            }}
            label={"Autofocus"}
          />
          <ToggleInput
            value={disabled}
            onChange={(val) => {
              setProp((props) => {
                props.disabled = val;
              });
            }}
            label={"Disabled"}
          />
          <ToggleInput
            value={required}
            onChange={(val) => {
              setProp((props) => {
                props.required = val;
              });
            }}
            label={"Required"}
          />
          <ToggleInput
            value={readonly}
            onChange={(val) => {
              setProp((props) => {
                props.readonly = val;
              });
            }}
            label={"Read Only"}
          />

          <SelectInput
            value={wrap}
            onChange={(val) => {
              setProp((props) => {
                props.wrap = val;
              });
            }}
            options={[
              {
                label: "Hard",
                value: "hard",
              },
              {
                label: "Soft",
                value: "soft",
              },
            ]}
            label={"Wrap"}
          />

          <NumberInput
            min={INPUT_LIMITS["form-textarea"].cols.minCols}
            max={INPUT_LIMITS["form-textarea"].cols.maxCols}
            value={cols}
            onChange={(val) => {
              setProp((props) => {
                props.cols = val;
              });
            }}
            label={"Columns"}
          />
          <NumberInput
            min={INPUT_LIMITS["form-textarea"].rows.minRows}
            max={INPUT_LIMITS["form-textarea"].rows.maxRows}
            resetValue={undefined}
            value={rows}
            onChange={(val) => {
              setProp((props) => {
                props.rows = val;
              });
            }}
            label={"Rows"}
          />
          <TextInput
            minLength={INPUT_LIMITS["form-textarea"].minlength}
            maxLength={INPUT_LIMITS["form-textarea"].maxlength}
            resetValue={undefined}
            value={value}
            onChange={(val) => {
              setProp((props) => {
                props.value = val;
              });
            }}
            label={"Value"}
          />
          <AlignInput
            isResponsive={true}
            resetValue={undefined}
            valueTablet={alignTablet}
            valueMobile={alignMobile}
            value={align}
            onChange={(val) => {
              setProp((props) => {
                props.align = val;
              });
            }}
            setValueTablet={(val) => {
              setProp((props) => {
                props.alignTablet = val;
              });
            }}
            setValueMobile={(val) => {
              setProp((props) => {
                props.alignMobile = val;
              });
            }}
            label={"Align"}
          />
          <TextInput
            minLength={INPUT_LIMITS["form-placeholder"].minlength}
            maxLength={INPUT_LIMITS["form-placeholder"].maxlength}
            value={placeholderText}
            onChange={(val) => {
              setProp((props) => {
                props.placeholderText = val;
              });
            }}
            label={"Placeholder"}
          />
          <UnitInput
            isResponsive={true}
            resetValue={undefined}
            value={inputWidth}
            valueMobile={inputWidthMobile}
            valueTablet={inputWidthTablet}
            onChange={(val) => {
              setProp((props) => {
                props.inputWidth = val;
              });
            }}
            setValueTablet={(val) => {
              setProp((props) => {
                props.inputWidthTablet = val;
              });
            }}
            setValueMobile={(val) => {
              setProp((props) => {
                props.inputWidthMobile = val;
              });
            }}
            label={"Width"}
          />
          <UnitInput
            isResponsive={true}
            resetValue={undefined}
            value={inputHeight}
            valueMobile={inputHeightMobile}
            valueTablet={inputHeightTablet}
            onChange={(val) => {
              setProp((props) => {
                props.inputHeight = val;
              });
            }}
            setValueTablet={(val) => {
              setProp((props) => {
                props.inputHeightTablet = val;
              });
            }}
            setValueMobile={(val) => {
              setProp((props) => {
                props.inputHeightMobile = val;
              });
            }}
            label={"Height"}
          />
        </Panel>
        <Panel
          isUpdated={isUpdatedFromDefault([{ key: showLabel }, { key: label }])}
          id={`block-setting-${id}-label`}
          title={"Label"}
          initialOpen={false}
        >
          <ToggleInput
            resetValue={undefined}
            value={showLabel}
            onChange={(val) => {
              setProp((props) => {
                props.showLabel = val;
              });
            }}
            label={"Show Label"}
          />
          {showLabel && (
            <>
              <TextInput
                minLength={INPUT_LIMITS["form-input-label"].minlength}
                maxLength={INPUT_LIMITS["form-input-label"].maxlength}
                resetValue={undefined}
                value={label}
                onChange={(val) => {
                  setProp((props) => {
                    props.label = val;
                  });
                }}
                label={"Label"}
              />
            </>
          )}
        </Panel>
        <Panel
          isUpdated={isUpdatedFromDefault([
            { key: containerWidth },
            { key: containerWidthMobile },
            { key: containerWidthTablet },
            { key: containerHeight },
            { key: containerHeightMobile },
            { key: containerHeightTablet },
            { key: containerMaxWidth },
            { key: containerMaxHeight },
          ])}
          id={`block-setting-${id}-container`}
          title={"Container"}
          initialOpen={false}
        >
          <UnitInput
            isResponsive={true}
            resetValue={undefined}
            value={containerWidth}
            valueMobile={containerWidthMobile}
            valueTablet={containerWidthTablet}
            onChange={(val) => {
              setProp((props) => {
                props.containerWidth = val;
              });
            }}
            setValueTablet={(val) => {
              setProp((props) => {
                props.containerWidthTablet = val;
              });
            }}
            setValueMobile={(val) => {
              setProp((props) => {
                props.containerWidthMobile = val;
              });
            }}
            label={"Width"}
          />
          <UnitInput
            isResponsive={true}
            resetValue={undefined}
            value={containerHeight}
            valueMobile={containerHeightMobile}
            valueTablet={containerHeightTablet}
            onChange={(val) => {
              setProp((props) => {
                props.containerHeight = val;
              });
            }}
            setValueTablet={(val) => {
              setProp((props) => {
                props.containerHeightTablet = val;
              });
            }}
            setValueMobile={(val) => {
              setProp((props) => {
                props.containerHeightMobile = val;
              });
            }}
            label={"Height"}
          />
          <UnitInput
            resetValue={undefined}
            value={containerMaxWidth}
            onChange={(val) => {
              setProp((props) => {
                props.containerMaxWidth = val;
              });
            }}
            label={"Max Width"}
          />
          <UnitInput
            resetValue={undefined}
            value={containerMaxHeight}
            onChange={(val) => {
              setProp((props) => {
                props.containerMaxHeight = val;
              });
            }}
            label={"Max Height"}
          />
        </Panel>
      </div>
    );
  }

  function StyleSettings() {
    return (
      <>
        <div className="g-settings__wrapper">
          <Panel
            isUpdated={isUpdatedFromDefault([
              { key: layoutMarginTablet },
              { key: layoutMarginMobile },
              { key: layoutMargin },
              { key: containerPaddingTablet },
              { key: containerPaddingMobile },
              { key: containerPadding },
              { key: containerBorder },
              { key: containerBorderRadius },
              { key: containerBoxShadow },
            ])}
            id={`block-setting-${id}-color`}
            title={"Color"}
            initialOpen={true}
          >
            <ColorInput
              value={inputColor}
              onChange={(val) => {
                setProp((props) => {
                  props.inputColor = val;
                });
              }}
              label={"Color"}
            />
            <ColorInput
              value={containerBackground}
              onChange={(val) => {
                setProp((props) => {
                  props.containerBackground = val;
                });
              }}
              label={"Background"}
            />
            <ColorInput
              value={inputPlaceholderColor}
              onChange={(val) => {
                setProp((props) => {
                  props.inputPlaceholderColor = val;
                });
              }}
              label={"Placeholder"}
            />
            <ColorInput
              value={inputBackgroundColor}
              onChange={(val) => {
                setProp((props) => {
                  props.inputBackgroundColor = val;
                });
              }}
              label={"Background"}
            />
            {showLabel && (
              <>
                <ColorInput
                  value={labelColor}
                  onChange={(val) => {
                    setProp((props) => {
                      props.labelColor = val;
                    });
                  }}
                  label={"Label"}
                />
                <ColorInput
                  value={labelRequiredColor}
                  onChange={(val) => {
                    setProp((props) => {
                      props.labelRequiredColor = val;
                    });
                  }}
                  label={"Label Required"}
                />
              </>
            )}
          </Panel>
        </div>
      </>
    );
  }

  function AdvancedSettings() {
    return (
      <>
        <Panel
          isUpdated={isUpdatedFromDefault([
            { key: layoutMarginTablet },
            { key: layoutMarginMobile },
            { key: layoutMargin },
            { key: containerPaddingTablet },
            { key: containerPaddingMobile },
            { key: containerPadding },
          ])}
          id={`block-setting-${id}-container-layout`}
          title={"Container Layout"}
          initialOpen={false}
        >
          <MarginInput
            label={"Margin"}
            isResponsive={true}
            valueTablet={layoutMarginTablet}
            valueMobile={layoutMarginMobile}
            value={layoutMargin}
            onChange={(val) => {
              setProp((props) => {
                props.layoutMargin = val;
              });
            }}
            setValueTablet={(val) => {
              setProp((props) => {
                props.layoutMarginTablet = val;
              });
            }}
            setValueMobile={(val) => {
              setProp((props) => {
                props.layoutMarginMobile = val;
              });
            }}
          />
          <PaddingInput
            label={"Padding"}
            isResponsive={true}
            valueTablet={containerPaddingTablet}
            valueMobile={containerPaddingMobile}
            value={containerPadding}
            onChange={(val) => {
              setProp((props) => {
                props.containerPadding = val;
              });
            }}
            setValueTablet={(val) => {
              setProp((props) => {
                props.containerPaddingTablet = val;
              });
            }}
            setValueMobile={(val) => {
              setProp((props) => {
                props.containerPaddingMobile = val;
              });
            }}
          />
        </Panel>
        <Panel
          isUpdated={isUpdatedFromDefault([
            { key: containerBorder },
            { key: containerBorderRadius },
          ])}
          id={`block-setting-${id}-container-border`}
          title={"Container Border"}
          initialOpen={false}
        >
          <BorderInput
            resetValue={{
              top: {
                width: "1px",
                style: "solid",
                color: "#666666",
              },
              left: {
                width: "1px",
                style: "solid",
                color: "#666666",
              },
              right: {
                width: "1px",
                style: "solid",
                color: "#666666",
              },
              bottom: {
                width: "1px",
                style: "solid",
                color: "#666666",
              },
            }}
            value={containerBorder}
            onChange={(val) => {
              setProp((props) => {
                props.containerBorder = val;
              });
            }}
            label={"Border"}
          />
          <BorderRadiusInput
            resetValue={undefined}
            value={containerBorderRadius}
            onChange={(val) => {
              setProp((props) => {
                props.containerBorderRadius = val;
              });
            }}
            label={"Border Radius"}
          />
        </Panel>
        <Panel
          isUpdated={isUpdatedFromDefault([{ key: containerBoxShadow }])}
          id={`block-setting-${id}-container-shadows`}
          title={"Container Shadows"}
          initialOpen={false}
        >
          <BoxShadowInput
            resetValue={undefined}
            value={containerBoxShadow}
            onChange={(val) => {
              setProp((props) => {
                props.containerBoxShadow = val;
              });
            }}
          />
        </Panel>
        <Panel
          isUpdated={isUpdatedFromDefault([
            { key: inputMarginTablet },
            { key: inputMarginMobile },
            { key: inputMargin },
            { key: inputPaddingTablet },
            { key: inputPaddingMobile },
            { key: inputPadding },
          ])}
          id={`block-setting-${id}-input-layout`}
          title={"Input Layout"}
          initialOpen={false}
        >
          <MarginInput
            label={"Margin"}
            isResponsive={true}
            valueTablet={inputMarginTablet}
            valueMobile={inputMarginMobile}
            value={inputMargin}
            onChange={(val) => {
              setProp((props) => {
                props.inputMargin = val;
              });
            }}
            setValueTablet={(val) => {
              setProp((props) => {
                props.inputMarginTablet = val;
              });
            }}
            setValueMobile={(val) => {
              setProp((props) => {
                props.inputMarginMobile = val;
              });
            }}
          />
          <PaddingInput
            label={"Padding"}
            isResponsive={true}
            valueTablet={inputPaddingTablet}
            valueMobile={inputPaddingMobile}
            value={inputPadding}
            onChange={(val) => {
              setProp((props) => {
                props.inputPadding = val;
              });
            }}
            setValueTablet={(val) => {
              setProp((props) => {
                props.inputPaddingTablet = val;
              });
            }}
            setValueMobile={(val) => {
              setProp((props) => {
                props.inputPaddingMobile = val;
              });
            }}
          />
        </Panel>
        <Panel
          isUpdated={isUpdatedFromDefault([
            { key: inputBorder },
            { key: inputBorderRadius },
          ])}
          id={`block-setting-${id}-input-border`}
          title={"Input Border"}
          initialOpen={false}
        >
          <BorderInput
            resetValue={undefined}
            value={inputBorder}
            onChange={(val) => {
              setProp((props) => {
                props.inputBorder = val;
              });
            }}
            label={"Border"}
          />
          <BorderRadiusInput
            resetValue={undefined}
            value={inputBorderRadius}
            onChange={(val) => {
              setProp((props) => {
                props.inputBorderRadius = val;
              });
            }}
            label={"Border Radius"}
          />
        </Panel>
        <Panel
          isUpdated={isUpdatedFromDefault([{ key: inputBoxShadow }])}
          id={`block-setting-${id}-input-shadows`}
          title={"Input Shadows"}
          initialOpen={false}
        >
          <BoxShadowInput
            resetValue={undefined}
            value={inputBoxShadow}
            onChange={(val) => {
              setProp((props) => {
                props.inputBoxShadow = val;
              });
            }}
          />
        </Panel>

        {supports &&
          supports.map((support) => {
            var Component = supportLibrary[support]?.component;

            if (!Component) {
              return;
            }

            return (
              <>
                <Component
                  block={{
                    id,
                    props,
                    dom,
                  }}
                  actions={{
                    setProp,
                  }}
                ></Component>
              </>
            );
          })}
      </>
    );
  }
  const tabs = [
    {
      label: "General",
      key: "general",
      children: GeneralSettings(),
      isUpdated: isUpdatedFromDefault([
        { key: name },
        { key: maxlength },
        { key: autofocus },
        { key: disabled },
        { key: required },
        { key: readonly },
        { key: wrap },
        { key: cols },
        { key: rows },
        { key: value },
        { key: alignTablet },
        { key: alignMobile },
        { key: align },
        { key: placeholderText },
        { key: inputWidth },
        { key: inputWidthMobile },
        { key: inputWidthTablet },
        { key: inputHeight },
        { key: inputHeightMobile },
        { key: inputHeightTablet },
        { key: showLabel },
        { key: label },
        { key: containerWidth },
        { key: containerWidthMobile },
        { key: containerWidthTablet },
        { key: containerHeight },
        { key: containerHeightMobile },
        { key: containerHeightTablet },
        { key: containerMaxWidth },
        { key: containerMaxHeight },
      ]),
    },
    {
      label: "Style",
      key: "style",
      children: StyleSettings(),
      isUpdated: isUpdatedFromDefault([
        { key: layoutMarginTablet },
        { key: layoutMarginMobile },
        { key: layoutMargin },
        { key: containerPaddingTablet },
        { key: containerPaddingMobile },
        { key: containerPadding },
        { key: containerBorder },
        { key: containerBorderRadius },
        { key: containerBoxShadow },
      ]),
    },
    {
      label: "Advanced",
      key: "advanced",
      children: AdvancedSettings(),
      isUpdated: isUpdatedFromDefault([
        { key: layoutMarginTablet },
        { key: layoutMarginMobile },
        { key: layoutMargin },
        { key: containerPaddingTablet },
        { key: containerPaddingMobile },
        { key: containerPadding },
        { key: containerBorder },
        { key: containerBorderRadius },
        { key: containerBoxShadow },
        { key: inputMarginTablet },
        { key: inputMarginMobile },
        { key: inputMargin },
        { key: inputPaddingTablet },
        { key: inputPaddingMobile },
        { key: inputPadding },
        { key: inputBorder },
        { key: inputBorderRadius },
        { key: inputBoxShadow },
      ]),
    },
  ];

  return (
    <div>
      <Tabs
        type="block-setting"
        id={`block-settings-tab-${id}`}
        items={tabs.map((item, i) => {
          return {
            label: item.label,
            key: item.key,
            children: item.children,
          };
        })}
      />
    </div>
  );
};
